import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { CreateSessionForm } from "../components/CreateSessionForm"
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  PageHeader,
  Spinner,
  useLoad,
} from "../components/ui"
import { api } from "../lib/api"

// One list for every session I have in this company, whatever workspace it
// belongs to — and the place a new one is created. Creation answers
// immediately and navigates straight to the session page, whose preparation
// screen follows the container coming up.

type SessionStatus = "creating" | "starting" | "running" | "idle" | "stopping" | "stopped" | "failed"

type SessionRow = {
  id: string
  workspaceId: string
  workspaceName?: string
  name: string
  status: SessionStatus
  error?: string | null
  lastActiveAt: string | null
  createdAt: string
}

type Workspace = { id: string; name: string; repoUrl: string | null }

const STATUS_TONE: Record<SessionStatus, string> = {
  creating: "text-warning",
  starting: "text-warning",
  running: "text-success",
  idle: "text-muted",
  stopping: "text-warning",
  stopped: "text-muted",
  failed: "text-danger",
}

export function SessionsPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const sessions = useLoad(() => api.get<{ sessions: SessionRow[] }>("/workspace-sessions"), [])
  const workspaces = useLoad(() => api.get<{ workspaces: Workspace[] }>("/workspaces"), [])
  // ?create=1 (or ?workspace=<id>) opens the form ready — the Workspaces
  // screen links here with the workspace already chosen.
  const [showForm, setShowForm] = useState(params.has("create") || params.has("workspace"))

  return (
    <>
      <PageHeader
        title="Sessions"
        subtitle="Your development sessions across every workspace of this company."
        action={
          <Button onClick={() => setShowForm(true)} disabled={showForm || !workspaces.data?.workspaces.length}>
            New Session
          </Button>
        }
      />

      <ErrorNote error={sessions.error} />

      {showForm && workspaces.data && (
        <CreateSessionForm
          workspaces={workspaces.data.workspaces}
          initialWorkspaceId={params.get("workspace") ?? undefined}
          onCancel={() => setShowForm(false)}
          onCreated={(id) => navigate(`/workspace-sessions/${id}`)}
        />
      )}

      {sessions.loading ? (
        <Spinner />
      ) : sessions.data?.sessions.length ? (
        <div className="flex flex-col gap-2">
          {sessions.data.sessions.map((session) => (
            <SessionRowCard key={session.id} session={session} onChanged={sessions.reload} />
          ))}
        </div>
      ) : (
        !showForm && (
          <Empty
            title="No sessions yet"
            hint={
              workspaces.data?.workspaces.length
                ? "Create one to start coding in an isolated environment."
                : "Create a workspace first — sessions are created from its repository."
            }
          />
        )
      )}
    </>
  )
}

function SessionRowCard({ session, onChanged }: { session: SessionRow; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)

  const act = async (verb: "start" | "stop" | "delete") => {
    if (verb === "delete" && !confirm("Delete this session? Its container and clone are removed.")) return
    setBusy(true)
    try {
      if (verb === "delete") await api.delete(`/workspace-sessions/${session.id}`)
      else await api.post(`/workspace-sessions/${session.id}/${verb}`)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  const preparing = session.status === "creating" || session.status === "starting"

  return (
    <Card className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="truncate font-medium text-foreground">{session.name}</p>
          <span className={`text-xs font-medium ${STATUS_TONE[session.status]}`}>
            {preparing && <span className="mr-1 inline-block size-2 animate-pulse rounded-full bg-warning align-middle" aria-hidden />}
            {session.status}
          </span>
        </div>
        {session.workspaceName && <p className="truncate text-sm text-muted">{session.workspaceName}</p>}
        <p className="mt-0.5 text-xs text-faint">
          {session.lastActiveAt
            ? `Last active ${new Date(session.lastActiveAt).toLocaleString()}`
            : `Created ${new Date(session.createdAt).toLocaleString()}`}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Link to={`/workspace-sessions/${session.id}`}>
          <Button variant="ghost">{preparing ? "Watch" : "Connect"}</Button>
        </Link>
        {(session.status === "stopped" || session.status === "failed") && (
          <Button variant="ghost" onClick={() => act("start")} disabled={busy}>
            Start
          </Button>
        )}
        {(session.status === "running" || session.status === "idle") && (
          <Button variant="ghost" onClick={() => act("stop")} disabled={busy}>
            Stop
          </Button>
        )}
        <Button variant="danger" onClick={() => act("delete")} disabled={busy}>
          Delete
        </Button>
      </div>
    </Card>
  )
}
